import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { Waypoint } from 'react-waypoint';
import Tabs, { Tab, TabList, useTabPanel } from '@atlaskit/tabs';
import { triggerRequestTabClicked, useCreateFireAnalyticsFromTrigger } from '../../common/utils/analytics';
import { Header } from './header';
import { LoadMoreWaypoint } from './load-more-waypoint';
import { TabListStickyHeader, TabWrapper, WaypointWrapper } from './styled';
export default function NotificationsLayout(_ref) {
  var fetchData = _ref.fetchData,
    listSize = _ref.listSize,
    fetchState = _ref.fetchState,
    canLoadMore = _ref.canLoadMore,
    notificationFilters = _ref.notificationFilters,
    NotificationListComponent = _ref.NotificationListComponent,
    ProductDropdownComponent = _ref.ProductDropdownComponent,
    isMarkAllAsReadVisible = _ref.isMarkAllAsReadVisible,
    handleMarkAllNotificationsRead = _ref.handleMarkAllNotificationsRead,
    notificationTabs = _ref.notificationTabs,
    headerStickyTop = _ref.headerStickyTop,
    _ref$spacing = _ref.spacing,
    spacing = _ref$spacing === void 0 ? 'comfortable' : _ref$spacing,
    toggleGroupedNotificationsView = _ref.toggleGroupedNotificationsView,
    isGroupedNotificationsViewOn = _ref.isGroupedNotificationsViewOn,
    HeaderFullPageButtonComponent = _ref.HeaderFullPageButtonComponent,
    product = _ref.product,
    ThirdPartyNotificationNudge = _ref.ThirdPartyNotificationNudge,
    emojiProvider = _ref.emojiProvider,
    mentionProvider = _ref.mentionProvider,
    onCommentCreatedFn = _ref.onCommentCreatedFn,
    isEligibleForBellNotifRepliesCtxExp = _ref.isEligibleForBellNotifRepliesCtxExp,
    onUnsavedCommentDraft = _ref.onUnsavedCommentDraft;
  var notifDrawerRef = useRef(null);
  var handleRequestTabAnalytics = useCreateFireAnalyticsFromTrigger(triggerRequestTabClicked);
  var intl = useIntl();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    tabStickyHeaderShowing = _useState2[0],
    setHeaderWaypointHidden = _useState2[1];
  var formatMessage = intl.formatMessage;
  var tabData = useMemo(function () {
    return notificationTabs.map(function (tab) {
      return {
        label: formatMessage(tab.label),
        category: tab.key,
        content: /*#__PURE__*/React.createElement(NotificationListComponent, {
          key: "category_".concat(tab.key),
          updateGroupingHeaders: function updateGroupingHeaders() {},
          product: product,
          ThirdPartyNotificationNudge: ThirdPartyNotificationNudge,
          emojiProvider: emojiProvider,
          mentionProvider: mentionProvider,
          onCommentCreatedFn: onCommentCreatedFn,
          isEligibleForBellNotifRepliesCtxExp: isEligibleForBellNotifRepliesCtxExp,
          onUnsavedCommentDraft: onUnsavedCommentDraft
        })
      };
    });
  }, [formatMessage, NotificationListComponent, notificationTabs, product, ThirdPartyNotificationNudge, emojiProvider, mentionProvider, onCommentCreatedFn, isEligibleForBellNotifRepliesCtxExp, onUnsavedCommentDraft]);
  var selectedTab = notificationTabs.findIndex(function (tab) {
    return tab.key === notificationFilters.filters.categoryFilter;
  });
  var handleTabSelect = function handleTabSelect(selected) {
    onUnsavedCommentDraft === null || onUnsavedCommentDraft === void 0 || onUnsavedCommentDraft(false);
    notificationFilters.handleSetCategoryFilter(selected.category);
    handleRequestTabAnalytics(selected.category, tabStickyHeaderShowing);
    if (notifDrawerRef.current) {
      notifDrawerRef.current.scrollIntoView({
        behavior: 'instant',
        block: 'start'
      });
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: notifDrawerRef
  }, /*#__PURE__*/React.createElement(Header, {
    isMarkAllAsReadVisible: isMarkAllAsReadVisible,
    handleMarkAllNotificationsRead: handleMarkAllNotificationsRead,
    toggleReadStateFilter: notificationFilters.toggleReadStateFilter,
    readStateFilter: notificationFilters.filters.readStateFilter,
    spacing: spacing,
    toggleGroupedNotificationsView: toggleGroupedNotificationsView,
    isGroupedNotificationsViewOn: isGroupedNotificationsViewOn,
    FullPageButton: HeaderFullPageButtonComponent
  }), /*#__PURE__*/React.createElement(Waypoint, {
    onEnter: function onEnter() {
      return setHeaderWaypointHidden(false);
    },
    onLeave: function onLeave() {
      return setHeaderWaypointHidden(true);
    }
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Tabs, {
    onChange: function onChange(index) {
      var selectedTab = tabData[index];
      handleTabSelect(selectedTab);
    },
    selected: selectedTab,
    id: "1wvhohfusvxqah2l3vkx8e",
    testId: "category-filter-tab",
    shouldUnmountTabPanelOnChange: true
  }, /*#__PURE__*/React.createElement(TabListStickyHeader, {
    top: headerStickyTop,
    isSticky: tabStickyHeaderShowing,
    spacing: spacing,
    "data-testid": "notifications-tabs-sticky-header-".concat(tabStickyHeaderShowing ? 'enabled' : 'disabled')
  }, /*#__PURE__*/React.createElement(TabList, null, tabData.map(function (tab) {
    return /*#__PURE__*/React.createElement(Tab, {
      testId: "notification-list-layout-".concat(tab.label),
      key: tab.category
    }, tab.label);
  }), ProductDropdownComponent)), tabData.map(function (tab) {
    return /*#__PURE__*/React.createElement(TabWrapper, {
      spacing: spacing
    }, /*#__PURE__*/React.createElement(NoFocusTabPanel, {
      key: tab.label
    }, tab.content));
  }))), /*#__PURE__*/React.createElement(WaypointWrapper, null, /*#__PURE__*/React.createElement(LoadMoreWaypoint, {
    listSize: listSize,
    fetchState: fetchState,
    canLoadMore: canLoadMore,
    fetchData: fetchData,
    readStateFilter: notificationFilters.filters.readStateFilter
  })));
}

/**
 * Overrides default TabPanel from @atlaskit/tabs and sets tabIndex = -1
 * Notifications are already focusable so the panel does not need focus.
 */
function NoFocusTabPanel(_ref2) {
  var children = _ref2.children;
  var tabPanelAttributes = useTabPanel();
  return /*#__PURE__*/React.createElement("div", _extends({}, tabPanelAttributes, {
    tabIndex: -1
  }), children);
}